import { z } from "zod";
import { Symbol_ } from "./primitives";

/**
 * One position.
 *
 * `manualValue` exists for holdings with no market price — SpaceX being the
 * case that prompted it. Those carry a value the owner types rather than one
 * the system can compute, and conflating the two would let an estimate look
 * like a quote.
 */
export const Holding = z.object({
  symbol: Symbol_,
  shares: z.number().nonnegative().finite(),
  manualValue: z.number().nonnegative().finite().optional(),
  note: z.string().max(200).optional(),
});
export type Holding = z.infer<typeof Holding>;

/** Bounded so one document cannot grow without limit. */
export const MAX_HOLDINGS = 200;

export const Holdings = z.array(Holding).max(MAX_HOLDINGS);
export type Holdings = z.infer<typeof Holdings>;

export const PORTFOLIO_SCHEMA_VERSION = 1;

/**
 * A stored portfolio.
 *
 * `id` equals `userId` on purpose. The container is partitioned on `/userId`,
 * so an identical id makes every access a point read or point write at 1 RU —
 * no query, no cross-partition fan-out, and a cost that stays flat however
 * many users exist.
 *
 * Both fields are always derived from the platform-injected principal header
 * and never from anything a caller sends. That is what makes it impossible to
 * address someone else's document: the key cannot be influenced.
 */
export const PortfolioDocument = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  schemaVersion: z.literal(PORTFOLIO_SCHEMA_VERSION),
  holdings: Holdings,
  updatedAt: z.string(),
});
export type PortfolioDocument = z.infer<typeof PortfolioDocument>;

/** What the browser sends. Deliberately carries no identity of its own. */
export const PortfolioUpdate = z.object({
  holdings: Holdings,
});
export type PortfolioUpdate = z.infer<typeof PortfolioUpdate>;

/** What the browser receives. */
export const PortfolioResponse = z.object({
  holdings: Holdings,
  updatedAt: z.string().nullable(),
  /** False when no document exists yet, which is not an error. */
  exists: z.boolean(),
});
export type PortfolioResponse = z.infer<typeof PortfolioResponse>;

export function buildDocument(userId: string, holdings: Holdings): PortfolioDocument {
  return PortfolioDocument.parse({
    id: userId,
    userId,
    schemaVersion: PORTFOLIO_SCHEMA_VERSION,
    holdings,
    updatedAt: new Date().toISOString(),
  });
}
